/**
 * i18n — minimal translation helper for Ion (and future Insights features).
 *
 * Design intent (locked in spark-design-lock.md §6): EN-first, CN-ready.
 * For F1 we ship English only; later phases swap in the China-fork
 * dictionary by setting `currentLocale` and adding `cn.json`.
 *
 * No external i18n library — keeping the surface tiny so we don't pay a
 * dependency cost for a feature whose only consumer right now is Ion.
 *
 *   t('ion.welcome.hello.named', { name: 'Björn' })
 *   → "Hi Björn, what's on your mind?"
 *
 * Missing keys log a warning and return the key string itself, which makes
 * gaps loud during development without crashing the UI.
 */

import en from './en.json'

const TRANSLATIONS = { en }

let currentLocale = 'en'
const missingWarned = new Set()

/**
 * Switch the active locale. Falls back to `en` if the requested locale is
 * not loaded.
 */
export function setLocale(locale) {
  if (TRANSLATIONS[locale]) {
    currentLocale = locale
    return true
  }
  console.warn('[i18n] locale not loaded:', locale)
  return false
}

export function getLocale() {
  return currentLocale
}

/**
 * Register a new locale dictionary (used by tests + China-fork integration).
 * Idempotent — re-registering replaces the previous dictionary.
 */
export function registerLocale(locale, dict) {
  TRANSLATIONS[locale] = dict
}

/**
 * Translate a key, optionally interpolating named params.
 *
 * @param {string} key
 * @param {Record<string, string | number>} [params]
 * @returns {string}
 */
export function t(key, params) {
  const dict = TRANSLATIONS[currentLocale] || TRANSLATIONS.en
  let template = dict[key]
  if (template == null) {
    if (!missingWarned.has(key)) {
      missingWarned.add(key)
      console.warn('[i18n] missing key:', key)
    }
    return key
  }
  if (params) {
    template = template.replace(/\{(\w+)\}/g, (_, name) =>
      params[name] != null ? String(params[name]) : `{${name}}`,
    )
  }
  return template
}
